import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";
import NeighborhoodSelect from "../components/NeighborhoodSelect";
import BuildingDetailPanel from "../components/BuildingDetailPanel";

export default function BuildingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [coopUploadResult, setCoopUploadResult] = useState(null);
  const [coopUploading, setCoopUploading] = useState(false);
  const [buildings, setBuildings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sortField, setSortField] = useState("display_address");
  const [sortDirection, setSortDirection] = useState("asc");
  const [ownershipType, setOwnershipType] = useState("");
  const [listingRecency, setListingRecency] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("");
  // Neighborhood multi-select
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);
  // neighborhoodDropdownOpen state removed — handled by NeighborhoodSelect component
  const [limit, setLimit] = useState(20);

  // Load all neighborhoods on mount (not filtered by model_active)
  useEffect(() => {
    const loadNeighborhoods = async () => {
      try {
        const data = await authFetch("/buildings/neighborhoods");
        setNeighborhoods(data.neighborhoods);
      } catch (error) {
        console.error("Error loading neighborhoods:", error);
      }
    };
    loadNeighborhoods();
  }, []);

  useEffect(() => {
    loadBuildings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search, sortField, sortDirection, ownershipType, listingRecency, verifiedFilter, selectedNeighborhoods]);

  // Handle building_id URL parameter - auto-load building details
  useEffect(() => {
    const buildingId = searchParams.get("building_id");
    if (buildingId) {
      loadBuildingDetail(parseInt(buildingId));
      // Clear the URL parameter after loading
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBuildings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.append("search", search);
      if (sortField) {
        params.append("sort_by", sortField);
        params.append("sort_dir", sortDirection);
      }
      if (selectedNeighborhoods.length > 0) {
        params.append("neighborhood_ids", selectedNeighborhoods.join(","));
      }
      if (ownershipType) params.append("ownership_type", ownershipType);
      if (listingRecency) params.append("listing_recency", listingRecency);
      if (verifiedFilter !== "") params.append("verified", verifiedFilter);

      const response = await authFetch(`/buildings/?${params}`);
      setBuildings(response.buildings);
      setTotal(response.total);
    } catch (error) {
      console.error("Error loading buildings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setPage(1);
  };

  const loadBuildingDetail = async (buildingId) => {
    setDetailLoading(true);
    try {
      const response = await authFetch(`/buildings/${buildingId}`);
      setSelectedBuilding(response);
    } catch (error) {
      console.error("Error loading building detail:", error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const clearFilters = () => {
    setOwnershipType("");
    setListingRecency("");
    setVerifiedFilter("");
    setSelectedNeighborhoods([]);
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <>
      <Header showLogout={true} />
      <div style={{ padding: "40px", maxWidth: "1800px", margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h1 style={{ marginBottom: "10px" }}>Buildings</h1>
            <p style={{ color: "#666", marginBottom: "10px" }}>
              Search buildings and add notes for the rent prediction agent
            </p>
            <Link
              to="/buildings/stats"
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                fontSize: "14px", fontWeight: 600, color: "#1976d2",
                textDecoration: "none", marginBottom: "30px",
              }}
            >
              📊 Building Stats &amp; Coverage →
            </Link>
          </div>

          {/* Co-op Approval CSV Upload */}
          <div style={{
            backgroundColor: "#f8f9fa", padding: "16px", borderRadius: "8px",
            border: "1px solid #dee2e6", minWidth: "280px",
          }}>
            <div style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px" }}>Approve Co-op Buildings</div>
            <div style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}>
              Upload CSV with <code>address,zipcode</code> columns
            </div>
            <input
              type="file"
              accept=".csv"
              style={{ fontSize: "12px", marginBottom: "8px" }}
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                setCoopUploading(true);
                setCoopUploadResult(null);
                try {
                  const formData = new FormData();
                  formData.append("file", file);
                  const resp = await fetch(`${process.env.REACT_APP_API_URL}/buildings/approve-coops`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                    body: formData,
                  });
                  const data = await resp.json();
                  if (!resp.ok) throw new Error(data.detail || "Upload failed");
                  setCoopUploadResult(data);
                } catch (err) {
                  setCoopUploadResult({ error: err.message });
                }
                setCoopUploading(false);
                e.target.value = "";
              }}
            />
            {coopUploading && <div style={{ fontSize: "12px", color: "#1976d2" }}>Processing...</div>}
            {coopUploadResult?.error && (
              <div style={{ fontSize: "12px", marginTop: "8px", padding: "8px", backgroundColor: "#ffebee", borderRadius: "4px", color: "#c62828" }}>
                {coopUploadResult.error}
              </div>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <form onSubmit={handleSearch} style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <input
              type="text"
              placeholder="Search by address or building name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{
                padding: "10px 16px",
                border: "1px solid #ddd",
                borderRadius: "6px",
                fontSize: "14px",
                width: "300px",
              }}
            />
            {/* Neighborhood Multi-Select Dropdown */}
            <NeighborhoodSelect
              neighborhoods={neighborhoods}
              selected={selectedNeighborhoods}
              onChange={setSelectedNeighborhoods}
              label={null}
            />
            <select
              value={ownershipType}
              onChange={(e) => { setOwnershipType(e.target.value); setPage(1); }}
              style={selectStyle}
            >
              <option value="">All Types</option>
              <option value="condo">Condo</option>
              <option value="coop">Co-op</option>
              <option value="rental">Rental</option>
              <option value="townhouse">Townhouse / House</option>
              <option value="multi">Multi Family</option>
            </select>
            <select
              value={listingRecency}
              onChange={(e) => { setListingRecency(e.target.value); setPage(1); }}
              style={selectStyle}
            >
              <option value="">Listings</option>
              <option value="active">Active Listings</option>
              <option value="last_5_years">Last 5 Years</option>
              <option value="older_than_5_years">Older Than 5 Years</option>
              <option value="none">No Listings</option>
            </select>
            <select
              value={verifiedFilter}
              onChange={(e) => { setVerifiedFilter(e.target.value); setPage(1); }}
              style={selectStyle}
            >
              <option value="">Verified Status</option>
              <option value="true">Verified</option>
              <option value="false">Not Verified</option>
            </select>
            <button
              type="submit"
              style={{
                padding: "10px 20px",
                backgroundColor: "#000",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Search
            </button>
            {(search || selectedNeighborhoods.length > 0 || ownershipType || listingRecency || verifiedFilter) && (
              <button
                type="button"
                onClick={clearFilters}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#f5f5f5",
                  color: "#333",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                Clear
              </button>
            )}
          </div>
        </form>

        {/* Results count */}
        <div style={{ marginBottom: "16px", color: "#666", fontSize: "14px" }}>
          {total.toLocaleString()} building{total !== 1 ? "s" : ""} found
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            Loading buildings...
          </div>
        ) : (
          <>
            {/* Buildings Table */}
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                overflow: "auto",
                marginBottom: "24px",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                }}
              >
                <thead>
                  <tr style={{ backgroundColor: "#f5f5f5" }}>
                    <th style={thStyleCompact}>ID</th>
                    <SortableHeader
                      label="Address"
                      field="display_address"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Neighborhood"
                      field="neighborhood"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <th style={thStyleCompact}>Zip</th>
                    <th style={thStyleCompact}>Block</th>
                    <th style={thStyleCompact}>Lot</th>
                    <SortableHeader
                      label="Units"
                      field="total_units"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <SortableHeader
                      label="Floors"
                      field="total_floors"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <th style={thStyleCompact}>Type</th>
                    <SortableHeader
                      label="Built"
                      field="year_built"
                      sortField={sortField}
                      sortDirection={sortDirection}
                      onSort={handleSort}
                    />
                    <th style={thStyleCompact}>Renovated</th>
                    <th style={thStyleCompact} title="Doorman">Doorman</th>
                    <th style={thStyleCompact} title="Elevator">Elev</th>
                    <th style={thStyleCompact} title="Concierge">Concierge</th>
                    <th style={thStyleCompact} title="Laundry">Laundry</th>
                    <th style={thStyleCompact} title="Storage">Storage</th>
                    <th style={thStyleCompact} title="Gym">Gym</th>
                    <th style={thStyleCompact} title="Pool">Pool</th>
                    <th style={thStyleCompact} title="Courtyard">Court yard</th>
                    <th style={thStyleCompact} title="Roof Deck">Roof Deck</th>
                    <th style={thStyleCompact} title="StreetEasy Amenities Imported">SE</th>
                    <th style={thStyleCompact} title="Building Tier">Tier</th>
                    <th style={thStyleCompact} title="Verified">Verified</th>
                  </tr>
                </thead>
                <tbody>
                  {buildings.map((b) => (
                    <tr
                      key={b.id}
                      style={{
                        borderBottom: "1px solid #e0e0e0",
                        cursor: "pointer",
                        backgroundColor:
                          selectedBuilding?.id === b.id ? "#e3f2fd" : "transparent",
                      }}
                      onClick={() => loadBuildingDetail(b.id)}
                      onMouseEnter={(e) => {
                        if (selectedBuilding?.id !== b.id) {
                          e.currentTarget.style.backgroundColor = "#fafafa";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedBuilding?.id !== b.id) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      <td style={tdStyleCompact}>{b.id}</td>
                      <td style={tdStyle}>
                        <Link to={`/buildings/${b.id}/edit`} style={{ color: "#000", textDecoration: "none" }}>
                          <strong>{b.display_address || "-"}</strong>
                        </Link>
                      </td>
                      <td style={tdStyleCompact}>{b.neighborhood || "-"}</td>
                      <td style={tdStyleCompact}>{b.postal_code || "-"}</td>
                      <td style={tdStyleCompact}>{b.block || "-"}</td>
                      <td style={tdStyleCompact}>{b.lot || "-"}</td>
                      <td style={tdStyleCompact}>{b.total_units || "-"}</td>
                      <td style={tdStyleCompact}>{b.total_floors || "-"}</td>
                      <td style={tdStyleCompact}>{b.ownership_type || "-"}</td>
                      <td style={tdStyleCompact}>{b.year_built || "-"}</td>
                      <td style={tdStyleCompact}>{b.year_renovated || "-"}</td>
                      <td style={tdStyleCompact}><BooleanBadgeCompact value={b.doorman} /></td>
                      <td style={tdStyleCompact}><BooleanBadgeCompact value={b.elevator} /></td>
                      <td style={tdStyleCompact}><BooleanBadgeCompact value={b.concierge} /></td>
                      <td style={tdStyleCompact}><BooleanBadgeCompact value={b.laundry} /></td>
                      <td style={tdStyleCompact}><BooleanBadgeCompact value={b.storage} /></td>
                      <td style={tdStyleCompact}><BooleanBadgeCompact value={b.gym} /></td>
                      <td style={tdStyleCompact}><BooleanBadgeCompact value={b.pool} /></td>
                      <td style={tdStyleCompact}><BooleanBadgeCompact value={b.courtyard} /></td>
                      <td style={tdStyleCompact}><BooleanBadgeCompact value={b.roof_deck} /></td>
                      <td style={tdStyleCompact}><BooleanBadgeCompact value={b.has_se_amenities} /></td>
                      <td style={tdStyleCompact}><TierBadge tier={b.building_tier} /></td>
                      <td style={tdStyleCompact}><VerifiedBadge verified={b.verified} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "12px" }}>
              <button onClick={() => setPage(1)} disabled={page === 1}
                style={{ ...paginationBtnStyle, opacity: page === 1 ? 0.5 : 1, cursor: page === 1 ? "not-allowed" : "pointer" }}>
                First
              </button>
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                style={{ ...paginationBtnStyle, opacity: page === 1 ? 0.5 : 1, cursor: page === 1 ? "not-allowed" : "pointer" }}>
                Previous
              </button>
              <span style={{ color: "#666" }}>Page {page} of {totalPages}</span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                style={{ ...paginationBtnStyle, opacity: page === totalPages ? 0.5 : 1, cursor: page === totalPages ? "not-allowed" : "pointer" }}>
                Next
              </button>
              <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
                style={{ ...paginationBtnStyle, opacity: page === totalPages ? 0.5 : 1, cursor: page === totalPages ? "not-allowed" : "pointer" }}>
                Last
              </button>
              <select value={limit} onChange={(e) => { setLimit(parseInt(e.target.value)); setPage(1); }}
                style={{ padding: "8px 12px", border: "1px solid #ddd", borderRadius: "6px", fontSize: "14px", backgroundColor: "#fff" }}>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
                <option value={200}>200 / page</option>
              </select>
            </div>
          </>
        )}

        {/* Detail Panel */}
        {selectedBuilding && (
          <BuildingDetailPanel
            building={selectedBuilding}
            loading={detailLoading}
            onClose={() => setSelectedBuilding(null)}
            onFeedbackAdded={() => {
              loadBuildingDetail(selectedBuilding.id);
              loadBuildings();
            }}
          />
        )}

        {/* Co-op Approval Results Modal */}
        {coopUploadResult && !coopUploadResult.error && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
            alignItems: "center", justifyContent: "center", zIndex: 2000,
          }} onClick={() => setCoopUploadResult(null)}>
            <div style={{
              backgroundColor: "#fff", borderRadius: "12px", padding: "30px",
              maxWidth: "700px", width: "90%", maxHeight: "80vh", overflow: "auto",
              boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
            }} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                <h2 style={{ margin: 0, fontSize: "20px" }}>Co-op Approval Results</h2>
                <button onClick={() => setCoopUploadResult(null)}
                  style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#666" }}>×</button>
              </div>

              {/* Summary */}
              <div style={{ display: "flex", gap: "16px", marginBottom: "20px" }}>
                <div style={{ flex: 1, padding: "12px", backgroundColor: "#e8f5e9", borderRadius: "6px", textAlign: "center" }}>
                  <div style={{ fontSize: "24px", fontWeight: "700", color: "#2e7d32" }}>{coopUploadResult.matched}</div>
                  <div style={{ fontSize: "12px", color: "#666" }}>Matched</div>
                </div>
                <div style={{ flex: 1, padding: "12px", backgroundColor: "#ffebee", borderRadius: "6px", textAlign: "center" }}>
                  <div style={{ fontSize: "24px", fontWeight: "700", color: "#c62828" }}>{coopUploadResult.unmatched}</div>
                  <div style={{ fontSize: "12px", color: "#666" }}>Not Found</div>
                </div>
                <div style={{ flex: 1, padding: "12px", backgroundColor: "#e3f2fd", borderRadius: "6px", textAlign: "center" }}>
                  <div style={{ fontSize: "24px", fontWeight: "700", color: "#1565c0" }}>{coopUploadResult.listings_approved}</div>
                  <div style={{ fontSize: "12px", color: "#666" }}>Listings to Review</div>
                </div>
              </div>

              {/* Detail Table — grouped by status, scrollable */}
              <div style={{ maxHeight: "400px", overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: "6px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <thead style={{ position: "sticky", top: 0, backgroundColor: "#fff", zIndex: 1 }}>
                  <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
                    <th style={{ textAlign: "left", padding: "8px", fontWeight: "600" }}>Address</th>
                    <th style={{ textAlign: "left", padding: "8px", fontWeight: "600" }}>Zip</th>
                    <th style={{ textAlign: "left", padding: "8px", fontWeight: "600" }}>Status</th>
                    <th style={{ textAlign: "right", padding: "8px", fontWeight: "600" }}>Listings</th>
                  </tr>
                </thead>
                <tbody>
                  {[...(coopUploadResult.details || [])]
                    .sort((a, b) => {
                      const order = { not_found: 0, approved: 1, already_approved: 2 };
                      return (order[a.status] ?? 9) - (order[b.status] ?? 9);
                    })
                    .map((d, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "8px" }}>{d.address}</td>
                      <td style={{ padding: "8px" }}>{d.zipcode || "—"}</td>
                      <td style={{ padding: "8px" }}>
                        <span style={{
                          padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: "600",
                          backgroundColor: d.status === "approved" ? "#e8f5e9"
                            : d.status === "already_approved" ? "#fff3e0"
                            : "#ffebee",
                          color: d.status === "approved" ? "#2e7d32"
                            : d.status === "already_approved" ? "#e65100"
                            : "#c62828",
                        }}>
                          {d.status === "approved" ? "Approved"
                            : d.status === "already_approved" ? "Already Approved"
                            : "Not Found"}
                        </span>
                      </td>
                      <td style={{ padding: "8px", textAlign: "right" }}>
                        {d.listings_moved != null ? d.listings_moved : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>

              <div style={{ marginTop: "20px", textAlign: "right" }}>
                <button onClick={() => { setCoopUploadResult(null); loadBuildings(); }}
                  style={{
                    padding: "8px 20px", backgroundColor: "#1976d2", color: "#fff",
                    border: "none", borderRadius: "6px", fontSize: "13px", fontWeight: "600", cursor: "pointer",
                  }}>
                  Close & Refresh
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function BooleanBadge({ value }) {
  if (value === null || value === undefined) return <span>-</span>;
  return (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: "4px",
        fontSize: "11px",
        fontWeight: "500",
        backgroundColor: value ? "#e8f5e9" : "#f5f5f5",
        color: value ? "#2e7d32" : "#666",
      }}
    >
      {value ? "Yes" : "No"}
    </span>
  );
}

function BooleanBadgeCompact({ value }) {
  if (value === null || value === undefined) return <span style={{ color: "#ccc" }}>-</span>;
  return (
    <span style={{ color: value ? "#2e7d32" : "#ccc", fontSize: "12px" }}>
      {value ? "✓" : "-"}
    </span>
  );
}

function TierBadge({ tier }) {
  if (!tier) return <span style={{ color: "#ccc" }}>-</span>;

  const tierColors = {
    "Walk up": { bg: "#f5f5f5", color: "#666" },
    "Walk up plus": { bg: "#efebe9", color: "#5d4037" },
    "E only": { bg: "#e3f2fd", color: "#1565c0" },
    "E plus": { bg: "#bbdefb", color: "#0d47a1" },
    "E/D only": { bg: "#e8f5e9", color: "#2e7d32" },
    "Low": { bg: "#fff3e0", color: "#e65100" },
    "Med": { bg: "#fce4ec", color: "#c2185b" },
    "High": { bg: "#f3e5f5", color: "#7b1fa2" },
    "Luxury": { bg: "#fff8e1", color: "#f57f17" },
  };

  const style = tierColors[tier] || { bg: "#f5f5f5", color: "#666" };

  return (
    <span style={{
      padding: "2px 6px",
      borderRadius: "4px",
      fontSize: "10px",
      fontWeight: "500",
      backgroundColor: style.bg,
      color: style.color,
      whiteSpace: "nowrap",
    }}>
      {tier}
    </span>
  );
}

function VerifiedBadge({ verified }) {
  if (!verified) return <span style={{ color: "#ccc" }}>-</span>;
  return (
    <span style={{
      padding: "2px 6px",
      borderRadius: "4px",
      fontSize: "10px",
      fontWeight: "500",
      backgroundColor: "#e8f5e9",
      color: "#2e7d32",
      whiteSpace: "nowrap",
    }}>
      Verified
    </span>
  );
}


function SortableHeader({ label, field, sortField, sortDirection, onSort }) {
  const isActive = sortField === field;

  return (
    <th
      style={{
        ...thStyle,
        cursor: "pointer",
        userSelect: "none",
      }}
      onClick={() => onSort(field)}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {label}
        <span
          style={{
            fontSize: "10px",
            color: isActive ? "#1976d2" : "#999",
          }}
        >
          {isActive ? (sortDirection === "asc" ? "▲" : "▼") : "▲▼"}
        </span>
      </div>
    </th>
  );
}

// Styles
const thStyle = {
  textAlign: "left",
  padding: "12px 16px",
  fontWeight: "600",
  fontSize: "13px",
  color: "#333",
  borderBottom: "2px solid #e0e0e0",
};

const thStyleCompact = {
  textAlign: "left",
  padding: "8px 6px",
  fontWeight: "600",
  fontSize: "11px",
  color: "#333",
  borderBottom: "2px solid #e0e0e0",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "12px 16px",
};

const tdStyleCompact = {
  padding: "8px 6px",
  fontSize: "12px",
  whiteSpace: "nowrap",
};

const selectStyle = {
  padding: "10px 16px",
  border: "1px solid #ddd",
  borderRadius: "6px",
  fontSize: "14px",
  backgroundColor: "#fff",
  cursor: "pointer",
};

const paginationBtnStyle = {
  padding: "8px 16px",
  backgroundColor: "#f5f5f5",
  border: "1px solid #ddd",
  borderRadius: "6px",
  fontSize: "14px",
};

